import { useStatsigClient } from "@statsig/react-bindings";

/**
 * Safe Statsig gate evaluation.
 *
 * The Statsig SDK reads `window.parent.origin` during init; on cross-origin
 * webview contexts (Discord embeds, some mobile in-app browsers) that throws a
 * SecurityError that escapes React render and forces the page to ErrorPage.
 *
 * Absorbs the failure in two layers:
 *   1. If useStatsigClient() is called outside a mounted StatsigProvider —
 *      because DevourProviders' STATSIG_CONTEXT_SAFE pre-check skipped the
 *      provider mount — the hook lookup itself throws. Caught here.
 *   2. If client.checkGate() throws (e.g., network failure during init), also
 *      caught.
 *
 * In both failure modes the gate is treated as `defaultValue`. Pass the value
 * you'd expect from Statsig's own default-on-failure behavior for that gate
 * — usually `false`.
 *
 * Rules-of-Hooks note: calling a hook inside try/catch is technically fragile
 * because React requires stable hook call order across renders. This is safe
 * here ONLY because `STATSIG_CONTEXT_SAFE` in DevourProviders is a module-level
 * IIFE evaluated once at import time — the provider mount status cannot change
 * during a session, so `useStatsigClient` either always throws or never throws
 * for the lifetime of the page. Do NOT change this to a runtime gate without
 * restructuring to avoid the try/catch around the hook.
 */
export function useGetStatsigGate(gateName: string, defaultValue: boolean): boolean {
    try {
        const { client } = useStatsigClient();
        return client?.checkGate(gateName) ?? defaultValue;
    } catch {
        return defaultValue;
    }
}
